import React, { useEffect, useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { schedulerService } from '../../../core/services/schedulerService';
import styles from './SchedulerSetting.module.scss';
import { JobCode, getJobCodes } from '../../../core/API/scheduler';
import { TimeOffReason } from '../../../types';
import { GeneralSettings } from '../../../types/scheduler';
import JobCodesList from './SchedulerSetting/JobCodesList';
import TimeOffReasonsList from './SchedulerSetting/TimeOffReasonsList';
import CreateJobCode from './SchedulerSetting/CreateJobCode';
import CreateTimeOffReason from './SchedulerSetting/CreateTimeOffReason';
import EditJobCode from './SchedulerSetting/EditJobCode';
import EditTimeOffReason from './SchedulerSetting/EditTimeOffReason';
import EditGeneralSettings from './SchedulerSetting/EditGeneralSettings';
import { Edit } from '@mui/icons-material';
import { usePermissionCheck } from '../../../core/hooks/usePermissionCheck';
import { CircularProgress, Box } from '@mui/material';

type ViewMode =
	| 'list'
	| 'createJobCode'
	| 'createTimeOffReason'
	| 'editJobCode'
	| 'editTimeOffReason'
	| 'editGeneralSettings';

const SchedulerSetting = () => {
	const { hasViewPermission, hasEditPermission, hasDeletePermission, hasExecutePermission } = usePermissionCheck();
	const canViewScheduler = hasViewPermission('Schedule');
	const canEditScheduler = hasEditPermission('Schedule');
	const canDeleteScheduler = hasDeletePermission('Schedule');
	const canExecuteScheduler = hasExecutePermission('Schedule');

	const [viewMode, setViewMode] = useState<ViewMode>('list');
	const [jobCodes, setJobCodes] = useState<JobCode[]>([]);
	const [timeOffReasons, setTimeOffReasons] = useState<TimeOffReason[]>([]);
	const [settings, setSettings] = useState<GeneralSettings | null>(null);
	const [editingJobCode, setEditingJobCode] = useState<JobCode | null>(null);
	const [editingTimeOffReason, setEditingTimeOffReason] = useState<TimeOffReason | null>(null);
	
	const [loadingSettings, setLoadingSettings] = useState(false);
	const [loadingJobCodes, setLoadingJobCodes] = useState(false);
	const [loadingTimeOffReasons, setLoadingTimeOffReasons] = useState(false);

	useEffect(() => {
		loadData();
	}, []);

	const loadData = async () => {
		setLoadingSettings(true);
		setLoadingJobCodes(true);
		setLoadingTimeOffReasons(true);
		
		try {
			const [jobCodesData, timeOffReasonsData, settingsData] = await Promise.all([
				getJobCodes(),
				schedulerService.getTimeOffReasons(),
				schedulerService.getSettings(),
			]);
			setJobCodes(jobCodesData || []);
			setTimeOffReasons(timeOffReasonsData || []);
			setSettings(settingsData || null);
			setViewMode('list');
		} finally {
			setLoadingSettings(false);
			setLoadingJobCodes(false);
			setLoadingTimeOffReasons(false);
		}
	};

	const handleBackClick = () => {
		setViewMode('list');
		setEditingJobCode(null);
		setEditingTimeOffReason(null);
	};

	// If user doesn't have view permission, don't render anything
	if (!canViewScheduler) {
		return (
			<div className={styles.schedulerSettings}>
				<Card className="card">
					<div style={{ padding: '20px', textAlign: 'center' }}>
						You do not have permission to view scheduler settings.
					</div>
				</Card>
			</div>
		);
	}

	if (viewMode === 'createJobCode') {
		// Compute next available job code number (max+1 or 1, not duplicate)
		let nextJobNumber = 1;
		if (jobCodes && jobCodes.length > 0) {
			const usedNumbers = jobCodes.map(jc => Number((jc as any).jobNumber)).filter(n => !isNaN(n));
			if (usedNumbers.length > 0) {
				// Always use max+1, not a duplicate
				nextJobNumber = Math.max(...usedNumbers) + 1;
			}
		}
		return (
			<div className={styles.schedulerSettings}>
				<CreateJobCode backButtonClick={handleBackClick} onJobCodeCreated={loadData} defaultJobNumber={nextJobNumber} />
			</div>
		);
	}

	if (viewMode === 'editJobCode' && editingJobCode) {
		return (
			<div className={styles.schedulerSettings}>
				<EditJobCode
					jobCode={editingJobCode}
					handleBackClick={handleBackClick}
					onJobCodeUpdated={loadData}
				/>
			</div>
		);
	}

	if (viewMode === 'createTimeOffReason') {
		return (
			<div className={styles.schedulerSettings}>
				<CreateTimeOffReason backButtonClick={handleBackClick} onTimeOffReasonCreated={loadData} />
			</div>
		);
	}

	if (viewMode === 'editTimeOffReason' && editingTimeOffReason) {
		return (
			<div className={styles.schedulerSettings}>
				<EditTimeOffReason
					reason={editingTimeOffReason}
					handleBackClick={handleBackClick}
					onTimeOffReasonUpdated={loadData}
				/>
			</div>
		);
	}

	if (viewMode === 'editGeneralSettings' && settings) {
		return (
			<div className={styles.schedulerSettings}>
				<EditGeneralSettings
					settings={settings}
					handleBackClick={handleBackClick}
					onSettingsUpdated={loadData}
				/>
			</div>
		);
	}

	return (
		<div className={styles.schedulerSettings}>
			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>General Settings</h2>
					{/* <Button
						className={styles.addButton}
						onClick={() => {
							setViewMode('editGeneralSettings');
						}}
					>
						Edit
					</Button> */}
				</div>
				<div className={styles.list}>
					<div className={styles.tableContainer}>
						{loadingSettings ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
								<CircularProgress size={20} color="inherit" />
							</Box>
						) : (
							<table className={styles.workplaceTable}>
								<thead>
									<tr>
										<th>Minor if Less Than</th>
										<th>Overtime Daily Hours</th>
										<th>Overtime Daily Warning</th>
										<th>Time Increment (min)</th>
										<th>Default Shift Length</th>
										<th style={{ width: 48, textAlign: 'center' }}>Edit</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>
											<span>{settings?.ageOfMinor ?? 18}</span>
										</td>
										<td>
											<span>{settings?.overtimeDailyHoursAt ?? 8}h</span>
										</td>
										<td>
											<span>{settings?.overtimeDailyWarningAt ?? 7}h</span>
										</td>
										<td>
											<span>{settings?.timeIncrement ?? 15}min</span>
										</td>
										<td>
											<span>{settings?.defaultShiftLengthHours ?? 8}h</span>
										</td>
										<td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
											<button
												className={styles.iconButton}
											title={canEditScheduler ? "Edit" : "You do not have permission to edit"}
											onClick={() => {
												setViewMode('editGeneralSettings');
											}}
											disabled={!canEditScheduler}
											style={{ opacity: canEditScheduler ? 1 : 0.5, cursor: canEditScheduler ? 'pointer' : 'not-allowed' }}
											>
												<Edit className={styles.editIcon} />
											</button>
										</td>
									</tr>
								</tbody>
							</table>
						)}
					</div>
				</div>
			</Card>

			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Job Codes</h2>
					<Button 
						className={styles.addButton} 
						onClick={() => setViewMode('createJobCode')}
						disabled={!canExecuteScheduler}
						title={canExecuteScheduler ? "Add Job Code" : "You do not have permission to add job codes"}
					>
						Add
					</Button>
				</div>
				<div className={styles.list}>
					{loadingJobCodes ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
							<CircularProgress size={20} color="inherit" />
						</Box>
					) : (
						<JobCodesList
							jobCodes={jobCodes}
							onJobCodeCreated={loadData}
							onEditClick={(jobCode) => {
								setEditingJobCode(jobCode);
								setViewMode('editJobCode');
							}}
							canEdit={canEditScheduler}
							canDelete={canDeleteScheduler}
						/>
					)}
				</div>
			</Card>

			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Time Off Reasons</h2>
					<Button 
						className={styles.addButton} 
						onClick={() => setViewMode('createTimeOffReason')}
						disabled={!canExecuteScheduler}
						title={canExecuteScheduler ? "Add Time Off Reason" : "You do not have permission to add time off reasons"}
					>
						Add
					</Button>
				</div>
				<div className={styles.list}>
					{loadingTimeOffReasons ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
							<CircularProgress size={20} color="inherit" />
						</Box>
					) : (
						<TimeOffReasonsList
							timeOffReasons={timeOffReasons}
							onTimeOffReasonCreated={loadData}
							onEditClick={(reason) => {
								setEditingTimeOffReason(reason);
								setViewMode('editTimeOffReason');
							}}
							canEdit={canEditScheduler}
							canDelete={canDeleteScheduler}
						/>
					)}
				</div>
			</Card>

			{/* <Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Schedule Permissions</h2>
				</div>
				<div className={styles.list}>
					<p>Define three scheduler-related permissions that can be mapped to security roles.</p>
					<label>
						<input
							type="checkbox"
							checked={permissions?.scheduler ?? false}
							onChange={(e) => setPermissions({ ...permissions, scheduler: e.target.checked })}
						/>
						Scheduler (view own schedule, execute requests)
					</label>
					<label>
						<input
							type="checkbox"
							checked={permissions?.schedulerAllEmployees ?? false}
							onChange={(e) =>
								setPermissions({ ...permissions, schedulerAllEmployees: e.target.checked })
							}
						/>
						Scheduler All Employees (view/create/publish schedules)
					</label>
					<label>
						<input
							type="checkbox"
							checked={permissions?.approveRequests ?? false}
							onChange={(e) =>
								setPermissions({ ...permissions, approveRequests: e.target.checked })
							}
						/>
						Approve Requests (approve schedule/time off requests)
					</label>
				</div>
			</Card> */}
		</div>
	);
};

export default SchedulerSetting;
